<template>
  <div
    class="bg-grey-background-contrast border-1 border-solid border-grey-divider-subdued rounded-xl -mx-16 -mt-40 -mb-24 px-16 py-24 md-1:-mx-24 md-1:px-24"
  >
    <h2 class="text-h4">{{ title }}</h2>
    <div v-if="subtitle">
      <hr class="my-16 border-grey-divider-subdued" />
      <h3 class="text-strong-body mt-16">{{ subtitle }}</h3>
    </div>
    <div class="flex flex-col items-center mt-16">
      <div class="flex items-center">
        <component
          :is="graphic.icon"
          v-for="(graphic, index) in graphics"
          :key="index"
          v-bind="graphic.props"
          :class="graphic.class"
        />
      </div>
      <p class="text-strong-body text-grey-body mt-8">{{ heading }}</p>
      <p class="text-body text-grey-body mt-4 mx-8 text-center">{{ body }}</p>
      <poppins-button
        class="profile-empty-state-btn mt-16 w-full"
        variant="outlined"
        size="medium"
        :value="btnText"
        :href="shouldUseSpaRoutes ? undefined : btnHref"
        :to="shouldUseSpaRoutes ? btnHref : undefined"
      />
    </div>
    <div v-if="additionalSection" class="mt-24 border-t-1 border-solid border-grey-divider-subdued">
      <h2 class="text-strong-body mt-24">{{ additionalSection.title }}</h2>
      <p class="text-body text-grey-body text-center mt-16 mx-8">{{ additionalSection.body }}</p>
      <poppins-button
        class="profile-empty-state-btn mt-16 w-full"
        variant="outlined"
        size="medium"
        :value="additionalSection.btnText"
        :href="additionalSection.btnHref"
      />
    </div>
  </div>
</template>

<script lang="ts">
import PoppinsButton from '@/components/button/Button.vue';
import FirstAidBadge from '@/graphics/badges/FirstAidBadge.vue';
import CprBadge from '@/graphics/badges/CPRBadge.vue';
import CalendarUpToDateIllustration from '@/graphics/illustrations/CalendarUpToDateIllustration.vue';
import LaundryIcon from '@/graphics/icons/LaundryIcon.vue';
import CarIcon from '@/graphics/icons/CarIcon.vue';
import ChildSupportIllustration from '@/graphics/illustrations/ChildSupportIllustration.vue';
import SpecialNeedsIllustration from '@/graphics/illustrations/SpecialNeedsIllustration.vue';
import CognitiveLoadIllustration from '@/graphics/illustrations/CognitiveLoadIllustration.vue';
import BookIllustration from '@/graphics/illustrations/BookIllustration.vue';

export default {
  name: 'ProfileEmptySectionTemplate',
  components: {
    PoppinsButton,
  },
  props: {
    variant: {
      type: String,
      default: '',
    },
    btnHref: {
      type: String,
      default: '',
    },
    shouldUseSpaRoutes: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    title(): string {
      if (!this.variant) return '';
      return this.variant.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    },
    heading(): string {
      switch (this.variant) {
        case 'certifications':
          return 'Boost your credibility';
        case 'schedule':
          return 'Your availability is missing';
        case 'other_services':
          return 'Go beyond the basics';
        case 'specialty_skills':
          return 'Your expertise sets you apart';
        case 'special_needs_experience':
          return 'Offer trusted support';
        case 'homework_help':
          return 'Boost their learning';
        case 'education':
          return 'Add your degree(s)';
        default:
          return '';
      }
    },
    subtitle(): string {
      if (this.variant === 'schedule') return 'General availability';
      return '';
    },
    body(): string {
      switch (this.variant) {
        case 'certifications':
          return 'Sharing your first aid and CPR certifications can help families feel confident in your care';
        case 'schedule':
          return 'Add your typical schedule to show when you’re usually available';
        case 'other_services':
          return 'Highlight your extra services';
        case 'specialty_skills':
          return 'Add any licenses or advanced training to stand out';
        case 'special_needs_experience':
          return 'Let families know about your experience providing specialized care';
        case 'homework_help':
          return 'Share how you can support with schoolwork and homework help';
        case 'education':
          return 'Sharing your education helps families get to know your background and strengths';
        default:
          return '';
      }
    },
    btnText(): string {
      switch (this.variant) {
        case 'certifications':
          return 'Add certification';
        case 'schedule':
          return 'Add general availability';
        case 'other_services':
          return 'Add other services';
        case 'specialty_skills':
          return 'Add specialty skills';
        case 'special_needs_experience':
          return 'Add special needs experience';
        case 'homework_help':
          return 'Add homework experience';
        case 'education':
          return 'Add education';
        default:
          return '';
      }
    },
    graphics(): any {
      switch (this.variant) {
        case 'certifications':
          return [
            {
              icon: CprBadge,
              props: { active: false, includeText: false, width: 65, height: 65 },
              class: '-mb-12',
            },
            {
              icon: FirstAidBadge,
              props: { active: false, includeText: false, width: 65, height: 65 },
              class: '-mb-12 -ml-8',
            },
          ];
        case 'schedule':
          return [{ icon: CalendarUpToDateIllustration, props: { width: 64, height: 64, outlineColor: '#8A8A8F' } }];
        case 'other_services':
          return [
            {
              icon: LaundryIcon,
              props: { color: '#8A8A8F' },
              class: 'w-52 h-52 rounded-full bg-white border-1 border-solid border-grey-divider-contrast p-12',
            },
            {
              icon: CarIcon,
              props: { width: 36, height: 36, color: '#8A8A8F' },
              class: 'w-52 h-52 rounded-full bg-white border-1 border-solid border-grey-divider-contrast p-12 ml-4',
            },
          ];
        case 'specialty_skills':
          return [{ icon: ChildSupportIllustration, props: { width: 64, height: 64, outlineColor: '#8A8A8F' } }];
        case 'special_needs_experience':
          return [{ icon: SpecialNeedsIllustration, props: { width: 64, height: 64, outlineColor: '#8A8A8F' } }];
        case 'homework_help':
          return [{ icon: CognitiveLoadIllustration, props: { width: 64, height: 64, outlineColor: '#8A8A8F' } }];
        case 'education':
          return [{ icon: BookIllustration, props: { width: 64, height: 64, outlineColor: '#8A8A8F' } }];
        default:
          return [];
      }
    },
    additionalSection(): any {
      if (this.variant === 'schedule') {
        return {
          title: 'Calendar availability',
          body: 'For when you have specific days that deviate from your general schedule',
          btnText: 'Add calendar availability',
          btnHref: '/availability',
        };
      }
      return null;
    },
  },
};
</script>

<style>
main:has(#provider-profile) .profile-empty-state-btn {
  background-color: transparent;
}
</style>
